import React, { useMemo, useState } from "react";
import {
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type SortingState,
    type VisibilityState,
} from "@tanstack/react-table";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Edit2, Trash2, /* UserPlus, */ Search, RotateCcwKey } from "lucide-react";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { Badge } from "../../ui/badge";
import { useListUser } from "../hooks/useListUser";
import AddUser from "./addUser";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../ui/dialog"; 
import { UserPlus } from "lucide-react";
import { useDeleteUser } from "../hooks/useDeleteUser";
import EditUser from "./editUser";
import { requestPasswordReset } from "../../auth/services/authService";


interface User {
    id: string;
    username: string;
    email: string;
    status: string;
    enabled: boolean;
    createdDate: string;
    lastModifiedDate: string;
    userGroups: string[];
}

const usergroupStyles: Record<"admin" | "executive" | "ecommerce" | "wholesale" | "retail", string> = {
    admin: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    executive: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    ecommerce: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
    wholesale: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    retail: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
};

const UserManagement: React.FC = () => {
    const { data: users = [], isLoading } = useListUser(); 
    const deleteUserMutation = useDeleteUser();

    const [searchQuery, setSearchQuery] = useState<string>(""); // State for search query
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = useState({});
    const [isAddUserOpen, setIsAddUserOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
    const [editTarget, setEditTarget] = useState<User | null>(null);
    const [resetStatus, setResetStatus] = useState<string>("");

    // Filter users based on the search query
    const filteredUsers = useMemo(() => {
        return users.filter(
            (user) =>
                user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
                user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                user.userGroups.some((group) =>
                    group.toLowerCase().includes(searchQuery.toLowerCase())
                )
        );
    }, [users, searchQuery]);

    const handleAdminResetPassword = async (username: string) => {
        setResetStatus("");
        try {
            await requestPasswordReset(username);
            setResetStatus("Reset code sent to user's email.");
        } catch (err: any) {
            setResetStatus(err.message || "Failed to send reset code.");
        }
    };

    const columns: ColumnDef<User>[] = useMemo(
        () => [
            {
                accessorKey: "username",
                header: "Username",
                cell: ({ row }) => (
                    <div className="font-medium">{row.getValue("username")}</div>
                ),
            },
            {
                accessorKey: "email",
                header: "Email",
                cell: ({ row }) => <div className="text-muted-foreground">{row.getValue("email")}</div>,
            },
            {
                accessorKey: "status",
                header: "Status",
                cell: ({ row }) => (
                    <Badge variant={row.getValue("status") === "CONFIRMED" ? "success" : "warning"}>
                        {row.getValue("status")}
                    </Badge>
                ),
            },
            {
                accessorKey: "enabled",
                header: "Enabled",
                cell: ({ row }) => (
                    <div>
                        {row.getValue("enabled") ? (
                            <Badge variant="success">Enabled</Badge>
                        ) : (
                            <Badge variant="destructive">Disabled</Badge>
                        )}
                    </div>
                ),
            },
            {
                accessorKey: "createdDate",
                header: "Created Date",
                cell: ({ row }) => (
                    <div>{new Date(row.getValue("createdDate")).toLocaleDateString()}</div>
                ),
            },
            {
                accessorKey: "lastModifiedDate",
                header: "Last Modified",
                cell: ({ row }) => (
                    <div>{new Date(row.getValue("lastModifiedDate")).toLocaleDateString()}</div>
                ),
            },
            {
                accessorKey: "userGroups",
                header: "User Groups",
                cell: ({ row }) => (
                    <div className="flex flex-wrap gap-1">
                        {(row.original.userGroups || []).map((group) => (
                            <Badge
                                key={group}
                                className={usergroupStyles[group as keyof typeof usergroupStyles] || "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"}
                            >
                                {group}
                            </Badge>
                        ))}
                    </div>
                ),
            },
            {
                id: "actions",
                header: "Actions",
                cell: ({ row }) => (
                    <div className="flex gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            title="Edit User"
                            onClick={() => setEditTarget(row.original)}
                        >
                            <Edit2 size={16} />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            title="Delete User"
                            onClick={() => setDeleteTarget(row.original)}
                            disabled={deleteUserMutation.isPending}
                        >
                            <Trash2 size={16} />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            title="Reset Password"
                            onClick={() => handleAdminResetPassword(row.original.username)}
                        >
                            <RotateCcwKey size={16} />
                        </Button>
                    </div>
                ),
            },
        ],
        [deleteUserMutation]
    );

    const table = useReactTable({
        data: filteredUsers, // Use filtered users for the table
        columns,
        state: {
            sorting,
            columnVisibility,
            rowSelection,
        },
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <div>
            <SummaryHeader title="User Management" />
            <Card>
                <CardHeader className="w-full">
                    <div className="flex justify-between items-center w-full">
                        <div className="relative w-64">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <Input
                                placeholder="Search username or email..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
                            <DialogTrigger asChild>
                                <Button size="sm" onClick={() => setIsAddUserOpen(true)}>
                                    <UserPlus className="h-4 w-4" />
                                    Add User
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <h2 className="text-lg font-medium">Add User</h2>
                                </DialogHeader>
                                <AddUser />
                            </DialogContent>
                        </Dialog>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                ) : table.getRowModel().rows.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow key={row.id}>
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            No results.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="flex items-center justify-end space-x-2 py-4">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            Next
                        </Button>
                    </div>
                </CardContent>
            </Card>
            {/* Confirmation Dialog */}
            <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <h2 className="text-lg font-medium">Confirm Delete</h2>
                    </DialogHeader>
                    <div>
                        Are you sure you want to delete user{" "}
                        <span className="font-semibold">{deleteTarget?.username}</span>?
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteTarget(null)}
                            disabled={deleteUserMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                if (deleteTarget) {
                                    deleteUserMutation.mutate(deleteTarget.username, {
                                        onSuccess: () => setDeleteTarget(null),
                                        onError: () => setDeleteTarget(null),
                                    });
                                }
                            }}
                            disabled={deleteUserMutation.isPending}
                        >
                            {deleteUserMutation.isPending ? "Deleting..." : "Delete"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
            {/* Edit User Dialog */}
            <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit User Groups</DialogTitle>
                        <DialogDescription>
                Select the groups you want to assign to this user.
            </DialogDescription>
                    </DialogHeader>
                    {editTarget && (
                        <EditUser
                            username={editTarget.username}
                            currentGroups={editTarget.userGroups}
                            onClose={() => setEditTarget(null)}
                        />
                    )}
                </DialogContent>
            </Dialog>
            {resetStatus && (
                <div className="mt-2 text-sm text-green-600">{resetStatus}</div>
            )}
        </div>
    );
};

export default UserManagement;